// ✅ src/pages/VehicleV2/LubeConsumptionReport.jsx
// Print only after Branch / Month / Year are selected; last search saved for print page.

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaEye,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com";
const PRINT_FILTER_KEY = "LUBE_CONSUMPTION_PRINT_FILTERS";

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const ID_COL_RE = /(^id$|_id$|typeid|categoryid|branchid)/i;
const IMPORTANT_COL_RE = /name|reg|code|vehicle|item|total|qty|branch|store|supplier/i;
const DAY_COL_RE = /^(d)?([1-9]|[12][0-9]|3[01])$/i;

const LubeConsumptionReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [itemCategory, setItemCategory] = useState({ id: "37", name: "LUBRICANTS", locked: true });
  const [itemNames, setItemNames] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [userLock, setUserLock] = useState({ branchLocked: false, branchValue: "" });

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);

  const [filters, setFilters] = useState({
    branch: "Select",
    month: "Select",
    year: "Select",
    itemCategoryId: "37",
    itemId: "Select",
  });

  const branchType = toStr(
    localStorage.getItem("branchtype") || localStorage.getItem("branchType") || "HO"
  );
  const loginBranchId = toStr(
    localStorage.getItem("branchid") || localStorage.getItem("branchId") || ""
  );
  const isBusy = metaLoading || loading;

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const displayColumns = useMemo(() => {
    const all = columns.length ? columns : rows[0] ? Object.keys(rows[0]) : [];
    if (!all.length) return [];
    const kept = all.filter(
      (c) => IMPORTANT_COL_RE.test(c) || DAY_COL_RE.test(String(c)) || /total/i.test(c)
    );
    const cleaned = kept.filter((c) => !ID_COL_RE.test(String(c)));
    return cleaned.length ? cleaned : all;
  }, [columns, rows]);

  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return rows.slice(indexOfFirstItem, indexOfLastItem);
  }, [rows, currentPage]);

  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;

  const handleChange = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  const toggleSupplier = (label) => {
    setSelectedSuppliers((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
  };

  const savePrintFilters = (f, suppliersSel = selectedSuppliers) => {
    const payload = {
      branchId: f.branch,
      branch: f.branch,
      month: f.month,
      year: f.year,
      itemCategoryId: f.itemCategoryId || "37",
      itemId: f.itemId || "Select",
      suppliers: suppliersSel,
    };
    try {
      sessionStorage.setItem(PRINT_FILTER_KEY, JSON.stringify(payload));
    } catch {
      // ignore
    }
    return payload;
  };

  const loadInit = async () => {
    try {
      setMetaLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/lubeConsumptionReport/init`, {
        params: { branchType, branchId: loginBranchId || undefined },
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");
      setBranches(data.branches || []);
      setMonths(data.months || []);
      setYears(data.years || []);
      setItemCategory(data.itemCategory || { id: "37", name: "LUBRICANTS", locked: true });
      setItemNames(data.itemNames || []);
      setSuppliers(data.suppliers || []);
      setUserLock(data.userLock || { branchLocked: false, branchValue: "" });
      setFilters((p) => ({
        ...p,
        branch: data.userLock?.branchLocked
          ? data.userLock.branchValue || p.branch || "Select"
          : p.branch || "Select",
        itemCategoryId: data.itemCategory?.id || "37",
      }));
    } catch (err) {
      console.error("init error:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to load init data");
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    loadInit();
  }, []);

  const fetchList = async (override = {}) => {
    const f = { ...filters, ...override };
    if (isSelect(f.month)) return toast.error("Please select Month");
    if (isSelect(f.year)) return toast.error("Please select Year");
    if (isSelect(f.branch)) return toast.error("Please select Branch");

    try {
      setLoading(true);
      savePrintFilters(f);
      const { data } = await axios.get(`${API_BASE}/api/lubeConsumptionReport/list`, {
        params: {
          branchId: f.branch,
          month: f.month,
          year: f.year,
          itemCategoryId: f.itemCategoryId || "37",
          itemId: f.itemId || "Select",
          suppliers: selectedSuppliers,
          date: "",
        },
      });
      if (!data?.success) throw new Error(data?.message || "List failed");
      setRows(data.rows || []);
      setColumns(data.columns || []);
      setTotal(data.total || 0);
      setCurrentPage(1);
      toast.success(`Found ${data.total ?? (data.rows || []).length} records`);
    } catch (err) {
      console.error("list error:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to fetch list");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchList();
  const handleViewAll = () => {
    setFilters((p) => ({ ...p, itemId: "Select" }));
    setSelectedSuppliers([]);
    fetchList({ itemId: "Select" });
  };
  const handleClearFilters = () => {
    setFilters((p) => ({
      ...p,
      month: "Select",
      year: "Select",
      itemId: "Select",
      branch: userLock?.branchLocked ? userLock.branchValue || "Select" : "Select",
      itemCategoryId: itemCategory?.id || "37",
    }));
    setSelectedSuppliers([]);
    setRows([]);
    setColumns([]);
    setTotal(0);
    setCurrentPage(1);
    toast.info("Filters cleared!");
  };

  const handlePrint = () => {
    let f = { ...filters };
    if (isSelect(f.branch) || isSelect(f.month) || isSelect(f.year)) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(PRINT_FILTER_KEY) || "{}");
        f = {
          ...f,
          branch: isSelect(f.branch) ? saved.branchId || saved.branch || f.branch : f.branch,
          month: isSelect(f.month) ? saved.month || f.month : f.month,
          year: isSelect(f.year) ? saved.year || f.year : f.year,
        };
      } catch {
        // ignore
      }
    }

    if (isSelect(f.branch) || isSelect(f.month) || isSelect(f.year)) {
      toast.error("Select Branch, Month and Year before Print");
      return;
    }

    const payload = savePrintFilters(f);
    const qs = new URLSearchParams({
      branchId: String(payload.branchId),
      month: String(payload.month),
      year: String(payload.year),
      itemCategoryId: String(payload.itemCategoryId || "37"),
      itemId: String(payload.itemId || "Select"),
      suppliersCsv: (payload.suppliers || []).join(","),
    }).toString();

    navigate(`/vehicle/LubeConsumptionReport_Print?${qs}`, { state: { filters: payload } });
  };

  const handleExport = () => toast.success("Export: hook your CSV/XLSX export here");

  return (
    <VehicleLayoutWrapper>
      <div
        className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">LUBE CONSUMPTION REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Lube Consumption Report
              </h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button
                onClick={handlePrint}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPrint /> Print
              </button>
              <button
                onClick={handleExport}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
              >
                <FaFileExport /> Export
              </button>
              <button
                onClick={loadInit}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
              >
                <FaSyncAlt /> Refresh
              </button>
              <button
                onClick={() => navigate(-1)}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
              >
                <FaArrowLeft /> Back
              </button>
              <button
                onClick={() => setShowFilters((s) => !s)}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" className="px-4 py-2 rounded-lg text-sm font-semibold border">
                    Clear
                  </button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border">
                    <FaEye /> View All
                  </button>
                  <button
                    onClick={handleSearch}
                    disabled={isBusy}
                    type="button"
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold ${buttonClass} text-white`}
                  >
                    <FaSearch /> {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={!!userLock?.branchLocked || isBusy}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={toStr(b.value)} value={toStr(b.value)}>
                        {toStr(b.label)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={fieldClass}>
                    <option value="Select">Select</option>
                    {months.map((m) => (
                      <option key={toStr(m.value)} value={toStr(m.value)}>
                        {toStr(m.label)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={fieldClass}>
                    <option value="Select">Select</option>
                    {years.map((y) => (
                      <option key={toStr(y.value)} value={toStr(y.value)}>
                        {toStr(y.label)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Item Category</label>
                  <input
                    className={fieldClass}
                    value={`${itemCategory?.name || "Item Category"} (${itemCategory?.id || "37"})`}
                    disabled
                  />
                </div>
                <div>
                  <label className={labelClass}>Item Name</label>
                  <select name="itemId" value={filters.itemId} onChange={handleChange} className={fieldClass}>
                    <option value="Select">Select</option>
                    {itemNames.map((it) => (
                      <option key={toStr(it.value)} value={toStr(it.value)}>
                        {toStr(it.label)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className={labelClass}>Suppliers</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 p-3 rounded-lg border max-h-40 overflow-auto">
                  {suppliers.map((s) => {
                    const label = toStr(s.label);
                    return (
                      <label key={label} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedSuppliers.includes(label)}
                          onChange={() => toggleSupplier(label)}
                        />
                        <span className="truncate">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
              Total Records: {rows.length}
            </div>
          </div>

          <div className="w-full overflow-x-auto rounded-lg border">
            {loading || metaLoading ? (
              <div className="p-16 text-center">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="p-16 text-center">No Records Found</div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {displayColumns.map((c) => (
                      <th key={c} className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">
                        {c}
                      </th>
                    ))}
                    <th className="px-3 py-3 text-left text-sm font-semibold">Print</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((row, idx) => (
                    <tr key={idx}>
                      {displayColumns.map((c) => (
                        <td key={c} className="px-3 py-3 text-sm whitespace-nowrap">
                          {toStr(row[c])}
                        </td>
                      ))}
                      <td className="px-3 py-3">
                        <button onClick={handlePrint} type="button" className="p-2 rounded">
                          <FaPrint className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex justify-between items-center">
              <div className="text-sm">
                Page {currentPage} / {totalPages}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} type="button" className="px-4 py-2 border rounded-lg">
                  Prev
                </button>
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} type="button" className="px-4 py-2 border rounded-lg">
                  Next
                </button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default LubeConsumptionReport;